import { useCallback, useEffect, useState } from 'react';

import { Box, CircularProgress } from '@mui/material';
import { useQuery } from 'react-query';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuthContext } from 'src/context/AuthContextProvider';
import { getMetaCarteirasFicFidcsWalletHistory } from 'src/services/meta-carteiras-fic-fidcs';
import {
  getFollowedFunds,
  getFundIndicators,
  getIndicatorsColumns,
  getOperationFicChildren,
  getOperationViewMetadata,
} from 'src/services/wallet';
import { followedFundsQueryKey } from 'src/services/wallet/queryKeys';
import { OperationViewMetadata } from 'src/types/wallet';
import { buildLoginRedirectPath } from 'src/utils/url';

import {
  FollowingViewProvider,
  useFollowingViewContext,
} from './FollowingViewContext';
import { filterFicIndicatorsForFollowingView } from './filterFicIndicators';
import { useMonitorContext } from '../../MonitorContextProvider';
import FicFidcCockpit from '../../components/FicFidcCockpit';
import { FicFidcCompleteViewModal } from '../../components/FicFidcCockpit/FicFidcCompleteViewModal';
import { useFicFidcCockpitConcentration } from '../../components/FicFidcCockpit/concentration/useFicFidcCockpitConcentration';
import { filterIndicatorsByCompetencyDate } from '../utils/filterIndicatorsByCompetencyDate';

interface FollowingViewDashboardProps {
  cockpitProps: Parameters<typeof FicFidcCockpit>[0];
  isCompleteViewOpen: boolean;
  onCloseCompleteView: () => void;
}

interface FollowingViewLocationState {
  operationMetadata?: Pick<OperationViewMetadata, 'operation'>;
}

function FollowingViewDashboard({
  cockpitProps,
  isCompleteViewOpen,
  onCloseCompleteView,
}: FollowingViewDashboardProps) {
  const { operationId, indicatorsCompetencyDate } = useFollowingViewContext();
  const concentration = useFicFidcCockpitConcentration(
    operationId,
    indicatorsCompetencyDate,
  );
  const {
    data: walletHistory,
    isLoading: isWalletHistoryLoading,
    isError: isWalletHistoryError,
  } = useQuery({
    queryKey: ['operation-fic-fidc-wallet-history', operationId],
    queryFn: () => getMetaCarteirasFicFidcsWalletHistory(operationId),
    enabled: isCompleteViewOpen && Boolean(operationId),
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  return (
    <>
      <FicFidcCockpit {...cockpitProps} />
      <FicFidcCompleteViewModal
        concentration={concentration}
        walletHistory={walletHistory?.data ?? []}
        isWalletHistoryLoading={isWalletHistoryLoading}
        isWalletHistoryError={isWalletHistoryError}
        open={isCompleteViewOpen}
        onClose={onCloseCompleteView}
      />
    </>
  );
}

export default function FollowingView() {
  const { operationId } = useParams<{ operationId: string }>();
  const history = useHistory();
  const { pathname, search, state } = useLocation<FollowingViewLocationState>();
  const { auth, handleLogout } = useAuthContext();
  const {
    indicatorsCompetencyDate,
    handleIndicatorsCompetencyDateChange,
    setBreadcrumbsItens,
  } = useMonitorContext();
  const [isCompleteViewOpen, setIsCompleteViewOpen] = useState(false);

  const hasPermission =
    !!auth?.user?.subscription?.plan?.permissions
      ?.operacoes_fidic_ativo_carteira;
  const isAuthReady =
    Boolean(auth?.isLoggedIn) && !auth.isLoading && Boolean(auth.user);
  const isGuardReady = isAuthReady && hasPermission;
  const preloadedMetadata =
    state?.operationMetadata?.operation.hashId === operationId
      ? {
          operation: state.operationMetadata.operation,
          summary: {
            fundsCount: 0,
            triggeredAlertsCount: 0,
            criticalCount: 0,
            alertCount: 0,
          },
        }
      : undefined;

  useEffect(() => {
    if (!auth?.isLoggedIn) {
      history.replace(buildLoginRedirectPath(pathname, search));
    }
  }, [auth?.isLoggedIn, history, pathname, search]);

  useEffect(() => {
    if (auth?.isLoggedIn && !auth.isLoading && auth.isFetched && !auth.user) {
      handleLogout();
    }
  }, [
    auth?.isLoggedIn,
    auth?.isLoading,
    auth?.isFetched,
    auth?.user,
    handleLogout,
  ]);

  useEffect(() => {
    if (!operationId) {
      history.replace('/monitor');
    }
  }, [operationId, history]);

  useEffect(() => {
    if (auth?.isLoggedIn && auth.user && !auth.isLoading && !hasPermission) {
      toast.error('Você não tem permissão para acessar esta análise.');
      history.replace('/monitor');
    }
  }, [auth?.isLoggedIn, auth?.isLoading, auth?.user, hasPermission, history]);

  const {
    data: followedFundsResponse,
    isLoading: isLoadingFollowedFunds,
    isError: isFollowedFundsError,
  } = useQuery({
    queryKey: followedFundsQueryKey(auth.user?.id),
    queryFn: getFollowedFunds,
    enabled: isGuardReady,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  const followedWallet = followedFundsResponse?.wallet;
  // O endpoint /wallet/followed-funds já é escopado ao usuário autenticado;
  // a checagem de owner é defesa adicional e fica inerte quando o payload
  // não traz `owner`.
  const isValidFollowedWallet =
    followedWallet?.purpose === 'followed_funds' &&
    (!followedWallet?.owner?.id || followedWallet.owner.id === auth.user?.id) &&
    !!followedWallet?.walletFunds?.some(
      (fund) =>
        !fund.parentWalletFund && fund.operation?.hashId === operationId,
    );

  const {
    data: ficChildren,
    isLoading: isLoadingFicChildren,
    isError: isFicChildrenError,
  } = useQuery({
    queryKey: ['operation-fic-children', operationId],
    queryFn: () => getOperationFicChildren(operationId || ''),
    enabled: isGuardReady && Boolean(isValidFollowedWallet),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const isFic = ficChildren?.isFic === true;

  const { data: metadata, isLoading: isLoadingMetadata } = useQuery({
    queryKey: ['operation-view-metadata', operationId],
    queryFn: () => getOperationViewMetadata(operationId || ''),
    enabled:
      isGuardReady && Boolean(isValidFollowedWallet) && !preloadedMetadata,
    initialData: preloadedMetadata,
    onError: (error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;

      if (status === 403) {
        toast.error('Você não tem permissão para acessar este fundo.');
      } else if (status === 400) {
        toast.error('Erro de integração ao carregar os dados do fundo.');
      } else {
        toast.error('Não foi possível carregar os dados do fundo.');
      }

      history.replace('/monitor');
    },
    refetchOnWindowFocus: false,
  });

  const walletId = followedWallet?.id ?? 0;
  const canLoadIndicators = isGuardReady && Boolean(isValidFollowedWallet);
  const { data: columnsData, isLoading: isLoadingColumns } = useQuery({
    queryKey: ['fic-following-indicators-columns', walletId],
    queryFn: () => getIndicatorsColumns(walletId),
    enabled: canLoadIndicators && walletId > 0,
    staleTime: 1000 * 60 * 60 * 6,
    refetchOnWindowFocus: false,
  });
  const {
    data: indicatorsData,
    isLoading: isLoadingIndicators,
    isFetching: isFetchingIndicators,
    isError: isErrorIndicators,
  } = useQuery({
    queryKey: [
      'fic-following-indicators',
      walletId,
      operationId,
      indicatorsCompetencyDate,
    ],
    queryFn: () => getFundIndicators(walletId, indicatorsCompetencyDate),
    enabled: canLoadIndicators && walletId > 0 && Boolean(columnsData),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const filteredIndicatorsData = filterFicIndicatorsForFollowingView(
    indicatorsData ?? [],
    operationId || '',
  ).map((item) =>
    filterIndicatorsByCompetencyDate(item, indicatorsCompetencyDate),
  );

  useEffect(() => {
    if (!isGuardReady || isLoadingFollowedFunds) return;

    if (isFollowedFundsError || !isValidFollowedWallet) {
      toast.error('Este fundo não está na sua carteira de fundos seguidos.');
      history.replace('/monitor');
    }
  }, [
    isGuardReady,
    isLoadingFollowedFunds,
    isFollowedFundsError,
    isValidFollowedWallet,
    history,
  ]);

  useEffect(() => {
    if (!isGuardReady || !isValidFollowedWallet || isLoadingFicChildren) return;

    if (isFicChildrenError) {
      toast.error('Não foi possível carregar os dados do FIC.');
      history.replace('/monitor');
      return;
    }

    if (ficChildren && !ficChildren.isFic) {
      toast.error('A análise detalhada está disponível apenas para FICs.');
      history.replace('/monitor');
    }
  }, [
    isGuardReady,
    isValidFollowedWallet,
    isLoadingFicChildren,
    isFicChildrenError,
    ficChildren,
    history,
  ]);

  const operationName = metadata?.operation.name ?? '';

  useEffect(() => {
    return () => {
      setBreadcrumbsItens([
        'Monitor',
        { title: 'Dashboard de Carteiras', link: '/monitor' },
      ]);
    };
  }, [setBreadcrumbsItens]);

  useEffect(() => {
    if (!metadata?.operation.name) return;

    setBreadcrumbsItens([
      { title: 'Monitor', link: '/monitor' },
      { title: 'Seguindo', link: '/monitor?tab=following' },
      {
        title: metadata.operation.name.toUpperCase(),
        link: `/monitor/wallet/following/${operationId}`,
      },
    ]);
  }, [metadata?.operation.name, operationId, setBreadcrumbsItens]);

  const onCompleteView = useCallback(() => {
    setIsCompleteViewOpen(true);
  }, []);

  const isLoadingView =
    !isGuardReady ||
    isLoadingFollowedFunds ||
    isLoadingFicChildren ||
    (isLoadingMetadata && !metadata);

  if (isLoadingView || !isFic) {
    return (
      <Box
        minHeight={400}
        display="flex"
        alignItems="center"
        justifyContent="center">
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (!isValidFollowedWallet || !metadata) {
    return null;
  }

  return (
    <FollowingViewProvider
      operationId={operationId || ''}
      walletId={followedWallet?.id ?? null}
      operationName={operationName}
      onCompleteView={onCompleteView}
      indicatorsCompetencyDate={indicatorsCompetencyDate}
      handleIndicatorsCompetencyDateChange={
        handleIndicatorsCompetencyDateChange
      }>
      <FollowingViewDashboard
        key={operationId}
        cockpitProps={{
          childOperationIds: ficChildren.children.map((child) => child.hashId),
          walletId,
          competencyDate: indicatorsCompetencyDate,
          indicatorsResponseData: filteredIndicatorsData,
          columnsData: columnsData ?? { data: [] },
          isLoadingColumns,
          isLoadingIndicators: isLoadingIndicators || isFetchingIndicators,
          isErrorIndicators,
          isEmptyIndicators: filteredIndicatorsData.length === 0,
        }}
        isCompleteViewOpen={isCompleteViewOpen}
        onCloseCompleteView={() => setIsCompleteViewOpen(false)}
      />
    </FollowingViewProvider>
  );
}
